/* eslint-disable complexity */
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import { useState, useEffect, useMemo, useCallback, DragEvent } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { SchemaDefinition, DuplicateRecordCriteria } from "./types";

type TransformMode = "none" | "uppercase" | "lowercase" | "capitalize" | "titlecase";

type RowStatus = "ok" | "update" | "insert" | "error";

type ParsedRow = Record<string, any> & {
  status: RowStatus;
  errorMessage?: string;
  existingId?: string; // ID of existing record if this is an update
};

type CSVImportDialogProps<T = Record<string, any>> = {
  open: boolean;
  onClose: () => void;
  onImport: (records: Omit<T, "id">[]) => void;
  existingRecords: T[];
  schemaDefinition: SchemaDefinition<T>;
  duplicateRecordCriteria: DuplicateRecordCriteria;
  title?: string;
};

const normalizeSpaces = (text: string): string => {
  return text.trim().replace(/\s+/g, " ");
};

const transformText = (text: string, mode: TransformMode): string => {
  const normalized = normalizeSpaces(text);

  switch (mode) {
    case "uppercase":
      return normalized.toUpperCase();
    case "lowercase":
      return normalized.toLowerCase();
    case "capitalize":
      return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
    case "titlecase":
      return normalized
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    default:
      return normalized;
  }
};

const normalizeValue = (value: any): string => {
  // Handle null, undefined, or missing values
  if (value === null || value === undefined || value === "") {
    return "";
  }
  if (typeof value === "string") {
    return value.toUpperCase().trim();
  }
  return String(value).toUpperCase().trim();
};

const checkDuplicate = <T extends Record<string, any>>(
  record: T,
  existingRecord: T,
  criteria: DuplicateRecordCriteria
): boolean => {
  if (criteria.logic === "AND") {
    // All fields must match
    return criteria.fields.every((field) => {
      // Handle missing fields - if field doesn't exist in record, treat as empty string
      const recordFieldValue = field in record ? record[field] : undefined;
      const existingFieldValue = field in existingRecord ? existingRecord[field] : undefined;
      
      const recordValue = normalizeValue(recordFieldValue);
      const existingValue = normalizeValue(existingFieldValue);
      return recordValue === existingValue;
    });
  } else {
    // Any field matches
    return criteria.fields.some((field) => {
      // Handle missing fields - if field doesn't exist in record, treat as empty string
      const recordFieldValue = field in record ? record[field] : undefined;
      const existingFieldValue = field in existingRecord ? existingRecord[field] : undefined;
      
      const recordValue = normalizeValue(recordFieldValue);
      const existingValue = normalizeValue(existingFieldValue);
      return recordValue === existingValue;
    });
  }
};

export const CSVImportDialog = <T extends Record<string, any>>({
  open,
  onClose,
  onImport,
  existingRecords,
  schemaDefinition,
  duplicateRecordCriteria,
  title,
}: CSVImportDialogProps<T>) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [csvText, setCsvText] = useState("");
  const [transformMode, setTransformMode] = useState<TransformMode>("none");
  const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
  const [importedCount, setImportedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const defaultTitle = title || `Import ${schemaDefinition.entityNamePlural || "Records"} from CSV`;

  // Build field map for quick lookup
  const fieldMap = useMemo(() => {
    const map = new Map<string, any>();
    schemaDefinition.fields.forEach((field) => {
      map.set(field.name, field);
    });
    return map;
  }, [schemaDefinition]);

  // Handle file drop
  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const text = event.target?.result;
          if (typeof text === "string") {
            setCsvText(text);
          }
        };
        reader.readAsText(file);
      }
    }
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result;
        if (typeof text === "string") {
          setCsvText(text);
        }
      };
      reader.readAsText(file);
    }
  }, []);

  // Parse CSV and validate
  const parseCSV = useMemo(() => {
    if (!csvText.trim()) {
      return [];
    }

    const lines = csvText.trim().split("\n");
    const parsed: ParsedRow[] = [];
    const seenInImport: T[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const parts = line.split(",").map((p) => p.trim());

      // Allow fewer columns (for backward compatibility with old CSV formats)
      // Only error if there are significantly more columns than expected
      if (parts.length > schemaDefinition.csvColumnOrder.length + 2) {
        const errorRecord: ParsedRow = {
          status: "error",
          errorMessage: `Invalid format. Expected up to ${schemaDefinition.csvColumnOrder.length} columns, found ${parts.length}`,
        };
        // Add empty values for display
        schemaDefinition.csvColumnOrder.forEach((fieldName) => {
          errorRecord[fieldName] = parts[schemaDefinition.csvColumnOrder.indexOf(fieldName)] || "";
        });
        parsed.push(errorRecord);
        continue;
      }

      // Parse values according to schema
      const record: Record<string, any> = {};
      let hasError = false;
      let errorMessage = "";

      schemaDefinition.csvColumnOrder.forEach((fieldName, index) => {
        const fieldDef = fieldMap.get(fieldName);
        if (!fieldDef) return;

        // If CSV has fewer columns, use undefined for missing optional fields
        // For required fields, this will be caught in validation below
        const rawValue = index < parts.length ? parts[index] : undefined;

        // Handle missing values (for backward compatibility with old CSV formats)
        if (rawValue === undefined || rawValue === "") {
          // For optional fields, use undefined/default
          if (!fieldDef.required) {
            record[fieldName] = fieldDef.defaultValue !== undefined ? fieldDef.defaultValue : undefined;
            return;
          } else {
            // Required field is missing - this is an error
            hasError = true;
            errorMessage = `Missing required field: ${fieldDef.label}`;
            record[fieldName] = "";
            return;
          }
        }

        // At this point, rawValue is guaranteed to be a non-empty string
        const rawValueStr: string = rawValue;

        // Apply transformation for string fields
        let value: any = rawValueStr;
        if (fieldDef.type === "string") {
          value = transformText(rawValueStr, transformMode);
        } else if (fieldDef.type === "boolean") {
          value = rawValueStr.toLowerCase() === "true" || rawValueStr === "1";
        } else if (fieldDef.type === "number") {
          value = parseFloat(rawValueStr);
          if (isNaN(value)) {
            hasError = true;
            errorMessage = `Invalid number for ${fieldDef.label}`;
          }
        } else if (fieldDef.type === "enum") {
          // Validate enum value
          if (fieldDef.enumValues && !fieldDef.enumValues.includes(rawValueStr)) {
            hasError = true;
            errorMessage = `Invalid value for ${fieldDef.label}. Expected one of: ${fieldDef.enumValues.join(", ")}`;
          }
          value = rawValueStr;
        } else if (fieldDef.type === "select") {
          // Validate select value (allow empty string for optional fields)
          if (rawValueStr && fieldDef.selectOptions) {
            const validValues = fieldDef.selectOptions.map((opt: { value: string; label: string }) => opt.value);
            if (!validValues.includes(rawValueStr)) {
              hasError = true;
              const validLabels = fieldDef.selectOptions.map((opt: { value: string; label: string }) => `${opt.label} (${opt.value})`).join(", ");
              errorMessage = `Invalid value for ${fieldDef.label}. Expected one of: ${validLabels}`;
            }
          }
          value = rawValueStr || undefined;
        }

        // Apply custom parser if provided
        if (fieldDef.valueParser) {
          try {
            value = fieldDef.valueParser(rawValue);
          } catch (e) {
            hasError = true;
            errorMessage = `Error parsing ${fieldDef.label}: ${e}`;
          }
        }

        record[fieldName] = value;
      });

      if (hasError) {
        parsed.push({
          ...record,
          status: "error",
          errorMessage,
        });
        continue;
      }

      // Check if this record matches an existing record (for update vs insert)
      const duplicateExisting = existingRecords.find((existing) =>
        checkDuplicate(record as T, existing, duplicateRecordCriteria)
      );

      // Check for duplicates within the CSV itself (last one wins)
      const duplicateInImportIndex = seenInImport.findIndex((seen) =>
        checkDuplicate(record as T, seen as T, duplicateRecordCriteria)
      );

      if (duplicateInImportIndex >= 0) {
        // Replace the previous occurrence with this one (last one wins)
        seenInImport[duplicateInImportIndex] = record as T;
        // Update the parsed row status
        const existingParsedIndex = parsed.findIndex((p) => 
          checkDuplicate(record as T, p as T, duplicateRecordCriteria)
        );
        if (existingParsedIndex >= 0) {
          parsed[existingParsedIndex] = {
            ...record,
            status: duplicateExisting ? ("update" as RowStatus) : ("insert" as RowStatus),
            existingId: duplicateExisting ? (duplicateExisting as any).id : undefined,
          } as ParsedRow;
        }
      } else {
        const validRecord: ParsedRow = {
          ...record,
          status: duplicateExisting ? ("update" as RowStatus) : ("insert" as RowStatus),
          existingId: duplicateExisting ? (duplicateExisting as any).id : undefined,
        };
        seenInImport.push(record as T);
        parsed.push(validRecord);
      }
    }

    return parsed;
  }, [csvText, transformMode, existingRecords, schemaDefinition, fieldMap, duplicateRecordCriteria]);

  useEffect(() => {
    setParsedData(parseCSV);
  }, [parseCSV]);

  const stats = useMemo(() => {
    const insertCount = parsedData.filter((row) => row.status === "insert").length;
    const updateCount = parsedData.filter((row) => row.status === "update").length;
    const errorCount = parsedData.filter((row) => row.status === "error").length;
    return { insertCount, updateCount, errorCount };
  }, [parsedData]);

  const handleNext = () => {
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleImport = () => {
    const validRows = parsedData
      .filter((row) => row.status === "insert" || row.status === "update")
      .map((row) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { status, errorMessage, existingId, ...data } = row;
        return {
          ...data,
          ...(existingId && { _existingId: existingId }), // Pass existing ID for updates
        } as Omit<T, "id"> & { _existingId?: string };
      });

    setImportedCount(validRows.length);
    setSkippedCount(0); // No longer skipping, we're updating/merging

    onImport(validRows as Omit<T, "id">[]);
    setStep(3);
  };

  const handleClose = () => {
    setCsvText("");
    setTransformMode("none");
    setParsedData([]);
    setStep(1);
    setImportedCount(0);
    setSkippedCount(0);
    setIsDragging(false);
    onClose();
  };

  const handleDone = () => {
    handleClose();
  };

  const getStatusIcon = (status: RowStatus) => {
    switch (status) {
      case "insert":
        return <CheckCircleIcon sx={{ color: "success.main" }} />;
      case "update":
        return <CheckCircleIcon sx={{ color: "info.main" }} />;
      case "error":
        return <ErrorIcon sx={{ color: "error.main" }} />;
    }
  };

  const getStatusLabel = (status: RowStatus) => {
    switch (status) {
      case "insert":
        return <Chip label="INSERT" color="success" size="small" />;
      case "update":
        return <Chip label="UPDATE" color="info" size="small" />;
      case "error":
        return <Chip label="ERROR" color="error" size="small" />;
    }
  };

  // Generate CSV format example
  const csvFormatExample = schemaDefinition.csvColumnOrder.join(",");

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {step === 1 && defaultTitle}
        {step === 2 && "Review Import"}
        {step === 3 && "Import Complete"}
      </DialogTitle>
      <DialogContent>
        {/* Step 1: Paste & Configure */}
        {step === 1 && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Paste CSV data or drag and drop a CSV file. Format: <code>{csvFormatExample}</code>
            </Typography>

            {/* Drag and drop zone */}
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                position: "relative",
                border: isDragging ? "2px dashed" : "1px solid",
                borderColor: isDragging ? "primary.main" : "divider",
                borderRadius: 1,
                backgroundColor: isDragging ? "action.hover" : "transparent",
                transition: "all 0.2s ease-in-out",
              }}
            >
              {isDragging && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    zIndex: 1,
                    borderRadius: 1,
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
                  <Typography variant="body1" color="primary">
                    Drop CSV file here
                  </Typography>
                </Box>
              )}
              <TextField
                multiline
                rows={10}
                fullWidth
                placeholder={`${csvFormatExample}\nvalue1,value2,value3\nvalue4,value5,value6`}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              />
            </Box>

            {/* File input button */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
              >
                Choose CSV File
                <input
                  type="file"
                  hidden
                  accept=".csv,text/csv"
                  onChange={handleFileInput}
                />
              </Button>
              <Typography variant="caption" color="text.secondary">
                or drag and drop a CSV file onto the text area above
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <InputLabel>Text Transformation (for string fields)</InputLabel>
              <Select
                value={transformMode}
                onChange={(e) => setTransformMode(e.target.value as TransformMode)}
                label="Text Transformation (for string fields)"
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="uppercase">UPPERCASE</MenuItem>
                <MenuItem value="lowercase">lowercase</MenuItem>
                <MenuItem value="capitalize">Capitalize</MenuItem>
                <MenuItem value="titlecase">Title Case</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Note: Text fields will be automatically trimmed and spaces normalized.
            </Typography>
          </Stack>
        )}

        {/* Step 2: Review & Validate */}
        {step === 2 && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {stats.insertCount + stats.updateCount} {stats.insertCount + stats.updateCount === 1 ? "item" : "items"} will be processed
              </Typography>
              {stats.insertCount > 0 && (
                <Typography variant="body2" color="success.main">
                  {stats.insertCount} {stats.insertCount === 1 ? "new record" : "new records"} will be inserted
                </Typography>
              )}
              {stats.updateCount > 0 && (
                <Typography variant="body2" color="info.main">
                  {stats.updateCount} {stats.updateCount === 1 ? "existing record" : "existing records"} will be updated
                </Typography>
              )}
              {stats.errorCount > 0 && (
                <Typography variant="body2" color="error.main">
                  {stats.errorCount} {stats.errorCount === 1 ? "error" : "errors"} found
                </Typography>
              )}
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {schemaDefinition.csvColumnOrder.map((fieldName) => {
                      const field = fieldMap.get(fieldName);
                      return (
                        <TableCell key={fieldName} sx={{ fontWeight: "bold" }}>
                          {field?.label || fieldName}
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedData.map((row, idx) => (
                    <TableRow key={idx}>
                      {schemaDefinition.csvColumnOrder.map((fieldName) => {
                        const field = fieldMap.get(fieldName);
                        const value = row[fieldName];
                        const displayValue = field?.valueFormatter
                          ? field.valueFormatter(value)
                          : String(value ?? "");
                        return <TableCell key={fieldName}>{displayValue}</TableCell>;
                      })}
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {getStatusIcon(row.status)}
                          {getStatusLabel(row.status)}
                          {row.errorMessage && (
                            <Typography variant="caption" color="text.secondary">
                              ({row.errorMessage})
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <Stack spacing={2} sx={{ mt: 1, alignItems: "center", py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: "success.main" }} />
            <Typography variant="h6">Successfully processed {importedCount} {importedCount === 1 ? "item" : "items"}</Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step === 1 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={!csvText.trim() || parsedData.length === 0}
            >
              Next
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <Button onClick={handleBack}>Back</Button>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleImport}
              variant="contained"
              disabled={stats.errorCount > 0 || (stats.insertCount === 0 && stats.updateCount === 0)}
            >
              Import
            </Button>
          </>
        )}
        {step === 3 && (
          <Button onClick={handleDone} variant="contained">
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
